import { FunctionComponent } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'preact/hooks';
import { GalleryArrows } from '../galleryArrows/galleryArrows';
import { MORE_VIDEOS_ICON_TYPES } from '../../icon/iconMapper';
import { GalleryVideo } from './galleryVideo/galleryVideo';
import { VideoData } from '../../../../common/store/store.types';
import { useStyles } from '../../../../common/hooks/useStyles';
import { getGalleryViewStyleObject } from './galleryView.style';
import { playerDataSelector } from '../../../../common/store/selectors/playerData.selector';
import { setInteractionOnEndState, setUserInteraction } from '../../store/moreVideos.actions';
import { moreVideosSelector } from '../../../../common/store/selectors/moreVideos.selector';
import { getLargeGalleryLeftArrowId, getLargeGalleryRightArrowId } from '../../htmlElements/htmlElementsIdGetter';

const RIGHT_ARIA_LABEL = 'Move to next thumbnail';
const LEFT_ARIA_LABEL = 'Move to previous thumbnail';

interface GalleryViewVariablesProps {
  numberOfVideoSpaces: number;
  updatedVideoList: Array<VideoData>;
}

export const GalleryView: FunctionComponent<GalleryViewVariablesProps> = props => {
  const { numberOfVideoSpaces, updatedVideoList } = props;

  const dispatch = useDispatch();
  const playerSize = useSelector(playerDataSelector.playerSize);
  const interactionOnEndState = useSelector(moreVideosSelector.interactionOnEndState);
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqueId);

  const [showRightArrow, setShowRightArrow] = useState(true);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [screenView, setScreenView] = useState(1);

  const styles = useStyles(getGalleryViewStyleObject, [playerSize]);
  const { galleryViewStyle, leftArrowWrapperStyle, rightArrowWrapperStyle, leftArrowStyle, rightArrowStyle } = styles;

  useEffect(() => {
    const updatedVideoListExists = updatedVideoList !== null;
    const isLastScreenView = updatedVideoListExists && (updatedVideoList.length - screenView + 1 <= numberOfVideoSpaces);
    const isFirstScreenView = screenView === 1;
    if (isLastScreenView) {
      setShowRightArrow(false);
    }
    if (isFirstScreenView) {
      setShowLeftArrow(false);
    }
  }, [numberOfVideoSpaces, screenView, updatedVideoList]);

  useEffect(() => {
    if (interactionOnEndState) {
      dispatch(setInteractionOnEndState(false));
    }
  }, [interactionOnEndState, dispatch]);

  const onClickLeftArrow = (): void => {
    setShowRightArrow(true);
    setScreenView(screenView - 1);
    dispatch(setUserInteraction('moreVideosCarouselClicked'));
    dispatch(setInteractionOnEndState(true));
  };

  const onClickRightArrow = (): void => {
    setScreenView(screenView + 1);
    setShowLeftArrow(true);
    dispatch(setUserInteraction('moreVideosCarouselClicked'));
    dispatch(setInteractionOnEndState(true));
  };

  return (
    <div style={galleryViewStyle}>
      { showLeftArrow
        && (
          <GalleryArrows
            icon={MORE_VIDEOS_ICON_TYPES.LEFT_ARROW_ICON}
            wrapperStyle={leftArrowWrapperStyle}
            arrowStyle={leftArrowStyle}
            onClick={onClickLeftArrow}
            id={getLargeGalleryLeftArrowId(playerUniqueId)}
            ariaLabel={LEFT_ARIA_LABEL}
          />
        ) }
      { updatedVideoList && updatedVideoList.map(videoData => (
        <GalleryVideo
          key={videoData.index}
          videoData={videoData}
          isFirstVideoOnList={updatedVideoList.indexOf(videoData) === 0}
          screenView={screenView}
          numberOfVideoSpaces={numberOfVideoSpaces}
          updatedVideoList={updatedVideoList}
        />
      ))}
      { showRightArrow
        && (
          <GalleryArrows
            icon={MORE_VIDEOS_ICON_TYPES.RIGHT_ARROW_ICON}
            wrapperStyle={rightArrowWrapperStyle}
            arrowStyle={rightArrowStyle}
            onClick={onClickRightArrow}
            id={getLargeGalleryRightArrowId(playerUniqueId)}
            ariaLabel={RIGHT_ARIA_LABEL}
          />
        ) }
    </div>
  );
};
